import { useQuery } from "@tanstack/react-query";
import { getLocalizedUrl } from "@/lib/localized-routes";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { ChevronUp, ChevronDown, Grip } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { 
  BarChart,
  Users,
  Star,
  Clock,
  Loader2,
  AlertTriangle,
  CreditCard,
  Zap,
  Settings,
  CheckCircle,
  HourglassIcon,
  BellRing,
  Calendar,
  Share2
} from "lucide-react";
import { type Task, type Client } from "@shared/schema";
import { Link } from "wouter";
import { ProgressRing } from "@/components/ui/progress-ring";
import { WeeklyCalendar } from "@/components/ui/weekly-calendar";
import { PlanInfoWidget } from "@/components/plan-info-widget";
import { DashboardOnboardingTrigger } from "@/components/onboarding/onboarding-trigger";
import { OnboardingChecklist } from "@/components/onboarding-checklist";
import { formatMoney, formatTax } from "@/lib/format-helpers";
import { apiRequest } from "@/lib/queryClient";
// Demo component removed

import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useMarketingMode } from "@/hooks/useMarketingMode";
// Demo banner removed

interface DashboardSection {
  id: string;
  title: string;
  visible: boolean;
  order: number;
}

export default function Dashboard() {
  const { user } = useAuth();
  const { toast } = useToast();
  // Use explicit namespaces to avoid bare useTranslation
  const { t } = useTranslation(["dashboard", "common", "reminders"]);
  const [showSectionSettings, setShowSectionSettings] = useState(false);
  const { isDemo: isMarketingMode } = useMarketingMode();

  // Ak nie je user, zobraz fallback
  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="p-4 text-sm text-muted-foreground">Redirecting...</div>
      </div>
    );
  }
  
  // Definícia sekcií dashboardu s nastaveniami viditeľnosti a poradia
  const [sections, setSections] = useState<DashboardSection[]>([
    { id: 'progressRings', title: t('dashboard:taskOverview'), visible: true, order: 1 },
    { id: 'weeklyCalendar', title: t('dashboard:weeklyPlanner'), visible: true, order: 2 },
    { id: 'stats', title: t('dashboard:statistics'), visible: true, order: 3 },
    { id: 'starredTasks', title: t('dashboard:starredTasks'), visible: true, order: 4 },
    { id: 'reminders', title: t('reminders:list.title'), visible: true, order: 5 },
    { id: 'subscription', title: t('dashboard:subscriptionStatus'), visible: true, order: 6 }
  ]);

  const { data: tasks, isLoading: tasksLoading, error: tasksError } = useQuery<Task[]>({
    queryKey: isMarketingMode ? ["/api/demo/tasks", isMarketingMode.language] : ["/api/tasks"],
    enabled: !!user, // Only fetch when user is loaded
    queryFn: async () => {
      if (isMarketingMode) {
        const response = await fetch(`/api/demo/tasks?lang=${isMarketingMode.language}`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      } else {
        const response = await apiRequest('GET', '/api/tasks?page=1&perPage=100');
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        // Očakávame { items: [...] } formát – vždy vráť pole
        const raw = data?.items ?? data;
        return Array.isArray(raw) ? raw : [];
      }
    },
    staleTime: 0, // No cache - always fresh data
    gcTime: 0, // No garbage collection time
    refetchOnWindowFocus: true, // Aktualizovať pri návrate na stránku
    refetchOnMount: true, // Always refetch on mount
    retry: 2, // Retry failed requests (reduced from 3)
    retryDelay: 1000, // 1 second delay between retries
  });

  // Query pre pripomienky
  const { data: remindersRaw, isLoading: remindersLoading } = useQuery({
    queryKey: ["/api/reminders"],
    enabled: !!user, // Only fetch when user is loaded
    queryFn: async () => {
      const response = await fetch('/api/reminders', {
        credentials: 'include',
        headers: { 'Accept': 'application/json' },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      const raw = data?.reminders ?? data;
      return Array.isArray(raw) ? raw : [];
    },
    staleTime: 0, // No cache - always fresh data
    gcTime: 0, // No garbage collection time
    refetchOnMount: true, // Always refetch on mount
    refetchOnWindowFocus: true, // Aktualizovať pri návrate na stránku
    retry: 1, // Retry only once for reminders (not critical)
  });

  const reminders = remindersRaw || [];

  // Billing data pre trial banner
  const { data: billingData } = useQuery<{
    subscription?: { status: string; trialEnd?: string | null } | null;
  }>({
    queryKey: ['/api/billing'],
    enabled: !!user && !isMarketingMode,
    staleTime: 1000 * 60 * 5,
  });

  const trialDaysLeft = (() => {
    const sub = billingData?.subscription;
    if (sub?.status === 'trial' && sub?.trialEnd) {
      const trialEnd = new Date(sub.trialEnd);
      const now = new Date();
      const days = Math.ceil((trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      return days > 0 ? days : null;
    }
    return null;
  })();

  useEffect(() => {
    if (remindersLoading || !Array.isArray(reminders)) return;
    const urgent = reminders.filter((reminder: any) => {
      if (!reminder?.dueAt) return false;
      return new Date(reminder.dueAt).getTime() <= Date.now();
    });
    if (urgent.length === 0) return;

    const latestDueAt = urgent
      .map((reminder: any) => reminder.dueAt)
      .filter(Boolean)
      .sort()
      .pop();
    const toastKey = `reminders-urgent-${user?.id || "anon"}`;
    const toastValue = `${urgent.length}-${latestDueAt || ""}`;
    if (sessionStorage.getItem(toastKey) === toastValue) return;

    toast({
      title: t('reminders:list.title') || 'Pripomienky',
      description: `Máte ${urgent.length} pripomienok na dnes alebo po termíne.`,
      variant: 'default',
    });
    sessionStorage.setItem(toastKey, toastValue);
  }, [reminders, remindersLoading, toast, t, user]);

  const { data: clients, isLoading: clientsLoading, error: clientsError } = useQuery<Client[]>({
    queryKey: isMarketingMode ? ["/api/demo/clients", isMarketingMode.language] : ["/api/clients"],
    enabled: !!user, // Only fetch when user is loaded
    queryFn: async () => {
      if (isMarketingMode) {
        const response = await fetch(`/api/demo/clients?lang=${isMarketingMode.language}`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      } else {
        const response = await apiRequest('GET', '/api/clients');
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        const raw = data?.items ?? data;
        return Array.isArray(raw) ? raw : [];
      }
    },
    staleTime: 60000, // 1 minute cache for clients
    refetchOnWindowFocus: false, // Clients not critical, don't refetch on focus
    retry: 1, // Retry only once for clients (not critical)
    retryDelay: 1000, // 1 second delay between retries
  });

  interface TaskStatistics {
    totalTasksAmount: number;
    amounts: {
      completed: number;
      pending: number;
      starred: number;
    };
    counts: {
      completed: number;
      pending: number;
      starred: number;
    };
    clientsWithTasks: Array<{
      clientName: string;
      totalAmount: number;
      taskCount: number;
    }>;
  }

  // Načítanie štatistík úloh
  const { data: taskStatistics, isLoading: statisticsLoading, error: statisticsError } = useQuery<TaskStatistics>({
    queryKey: isMarketingMode ? ["/api/demo/statistics", isMarketingMode.language] : ["/api/tasks/statistics"],
    enabled: !!user, // Only fetch when user is loaded
    queryFn: async () => {
      if (isMarketingMode) {
        const response = await fetch(`/api/demo/statistics?lang=${isMarketingMode.language}`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      } else {
        const response = await apiRequest('GET', '/api/tasks/statistics');
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        // Očakávame { totalTasksAmount, counts, amounts, clientsWithTasks }
        return data && typeof data === 'object' ? data : {};
      }
    },
    staleTime: 0, // No cache - always fresh data
    gcTime: 0, // No garbage collection time
    refetchOnWindowFocus: true, // Aktualizovať pri návrate na stránku
    refetchOnMount: true, // Always refetch on mount
    retry: 2, // Retry failed requests (reduced from 3)
    retryDelay: 1000, // 1 second delay between retries
  });

  // Zobraz dashboard keď máme štatistiky ALEBO keď tasks dokončil – neblokuj donekonečna
  const hasAnyData = !!taskStatistics || tasks !== undefined;
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  useEffect(() => {
    if (hasAnyData || !user) return;
    const t = setTimeout(() => setLoadingTimeout(true), 12000);
    return () => clearTimeout(t);
  }, [hasAnyData, user]);
  const isLoading = (tasksLoading && !taskStatistics && !hasAnyData) && !loadingTimeout;
  
  // Ignore 403/Forbidden errors for clients (user might not have permission)
  const isForbiddenClients = clientsError && 
    ((clientsError as any)?.message?.includes('Forbidden') || 
     (clientsError as any)?.message?.includes('403'));
  
  // Only block when tasks fail AND we have no statistics - ak máme štatistiky, zobrazíme dashboard
  const error = (tasksError && !taskStatistics) || (clientsError && !isForbiddenClients);

  // Normalizujeme tasks - podporujeme oba formáty (camelCase aj snake_case)
  const normalizedTasks = useMemo(() => {
    return Array.isArray(tasks) ? tasks.map((task: any) => ({
      ...task,
      isStarred: task.isStarred !== undefined ? task.isStarred : (task.is_starred ?? false),
      isInvoiced: task.isInvoiced !== undefined ? task.isInvoiced : (task.is_invoiced ?? false),
      dueDate: task.dueDate || task.due_date || null,
      clientId: task.clientId || task.client_id || null,
      taskType: task.taskType || task.task_type || 'standard',
      hoursWorked: task.hoursWorked || task.hours_worked || 0,
    })) : [];
  }, [tasks]);

  // Filtrujeme vyfaktúrované úlohy z označených hviezdičkou
  const starredTasks = useMemo(() => normalizedTasks.filter(task => task.isStarred && !task.isInvoiced), [normalizedTasks]);
  const completedTasks = useMemo(() => normalizedTasks.filter(task => task.status === 'completed'), [normalizedTasks]);
  const pendingTasks = useMemo(() => normalizedTasks.filter(task => task.status === 'pending'), [normalizedTasks]);
  
  // Pre progress rings: preferuj taskStatistics keď má dáta (API štatistiky fungujú aj keď /api/tasks vracia prázdne)
  const totalTasks = taskStatistics?.counts?.total ?? normalizedTasks.length;
  const displayCompleted = taskStatistics?.counts?.completed ?? completedTasks.length;
  const displayStarred = taskStatistics?.counts?.starred ?? starredTasks.length;
  const displayPending = taskStatistics?.counts?.pending ?? pendingTasks.length;
  
  // Počty pre štatistiky
  const starredPercentage = totalTasks > 0 ? (displayStarred / totalTasks) * 100 : 0;
  const completedPercentage = totalTasks > 0 ? (displayCompleted / totalTasks) * 100 : 0;
  const pendingPercentage = totalTasks > 0 ? (displayPending / totalTasks) * 100 : 0;
  
  // Výpočet celkovej sumy za všetky úlohy
  const calculateTotalAmount = (tasks: Task[]) => {
    // Bezpečnostná kontrola - tasks musí byť pole
    if (!Array.isArray(tasks)) {
      console.warn('🔧 [Dashboard] calculateTotalAmount: tasks is not an array:', tasks);
      return 0;
    }
    
    return tasks.reduce((sum, task) => {
      // Use API calculated totals if available
      if (task.subtotal_cents !== undefined && task.total_cents !== undefined) {
        // If user is not VAT payer, use subtotal (without VAT), otherwise use total (with VAT)
        return sum + ((user?.isVatPayer === true) ? (task.total_cents / 100) : (task.subtotal_cents / 100));
      }
      // Fallback to simple calculation
      const client = Array.isArray(clients) ? clients.find(c => c.id === task.clientId) : undefined;
      const hourlyRate = client?.hourlyRate || task.hourlyRate || 0;
      const hoursWorked = task.hoursWorked || 0;
      const baseAmount = hourlyRate * hoursWorked;
      
      // Add items amount if available
      let itemsAmount = 0;
      if (task.items && Array.isArray(task.items)) {
        itemsAmount = task.items.reduce((itemSum, item) => {
          const quantity = Number(item.quantity || 0);
          const unitPrice = Number(item.unitPrice || 0);
          return itemSum + (quantity * unitPrice);
        }, 0);
      }
      
      const totalWithoutVat = baseAmount + itemsAmount;
      
      // Add VAT if user is VAT payer
      if (user?.isVatPayer === true && totalWithoutVat > 0) {
        const vatRate = Number(user?.companyVatRate || 20);
        const vatAmount = totalWithoutVat * vatRate / 100;
        return sum + totalWithoutVat + vatAmount;
      }
      
      return sum + totalWithoutVat;
    }, 0);
  };

  const totalTasksAmount = calculateTotalAmount(normalizedTasks);
  
  // Reaktívne aktualizácie po zmene úloh
  useEffect(() => {
      console.log('🔧 [Dashboard] Tasks updated, recalculating statistics');
    console.log('🔧 [Dashboard] Total tasks:', normalizedTasks.length);
      console.log('🔧 [Dashboard] Completed tasks:', completedTasks.length);
      console.log('🔧 [Dashboard] Starred tasks:', starredTasks.length);
    console.log('🔧 [Dashboard] Starred tasks details:', starredTasks.map(t => ({ 
      id: t.id, 
      title: t.title, 
      isStarred: t.isStarred, 
      isInvoiced: t.isInvoiced,
      dueDate: t.dueDate,
    })));
    if (normalizedTasks.length > 0) {
      console.log('🔧 [Dashboard] Sample normalized task:', {
        id: normalizedTasks[0].id,
        title: normalizedTasks[0].title,
        isStarred: normalizedTasks[0].isStarred,
        isInvoiced: normalizedTasks[0].isInvoiced,
        dueDate: normalizedTasks[0].dueDate,
      });
      console.log('🔧 [Dashboard] Total amount:', totalTasksAmount);
    }
  }, [normalizedTasks, completedTasks, starredTasks, totalTasksAmount]);
  
  // Ukladanie nastavení sekcií do localStorage
  useEffect(() => {
    localStorage.setItem('dashboardSections', JSON.stringify(sections));
  }, [sections]);
  
  // Animácie odstránené pre lepšiu výkonnosť
  
  // Helper funkcia na kontrolu viditeľnosti sekcie
  const isSectionVisible = (id: string) => {
    return sections.find(section => section.id === id)?.visible ?? true;
  };
  
  // Získanie poradia sekcie
  const getSectionOrder = (id: string) => {
    return sections.find(section => section.id === id)?.order ?? 999;
  };
  
  // Zmena viditeľnosti sekcie
  const toggleSectionVisibility = (id: string) => {
    setSections(sections.map(section => 
      section.id === id ? { ...section, visible: !section.visible } : section
    ));
  };
  
  // Posun sekcie hore (zníženie poradia)
  const moveSectionUp = (id: string) => {
    const currentSection = sections.find(s => s.id === id);
    if (!currentSection) return;
    
    const sectionAbove = sections
      .filter(s => s.order < currentSection.order)
      .sort((a, b) => b.order - a.order)[0];
      
    if (!sectionAbove) return;
    
    setSections(sections.map(section => {
      if (section.id === id) {
        return { ...section, order: sectionAbove.order };
      }
      if (section.id === sectionAbove.id) {
        return { ...section, order: currentSection.order };
      }
      return section;
    }));
  };
  
  // Posun sekcie dole (zvýšenie poradia)
  const moveSectionDown = (id: string) => {
    const currentSection = sections.find(s => s.id === id);
    if (!currentSection) return;
    
    const sectionBelow = sections
      .filter(s => s.order > currentSection.order)
      .sort((a, b) => a.order - b.order)[0];
      
    if (!sectionBelow) return;
    
    setSections(sections.map(section => {
      if (section.id === id) {
        return { ...section, order: sectionBelow.order };
      }
      if (section.id === sectionBelow.id) {
        return { ...section, order: currentSection.order };
      }
      return section;
    }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    // Get error message
    const errorMessage = (error as any)?.message || t('dashboard:errorLoading');
    
    return (
      <div className="flex flex-col items-center justify-center gap-4 min-h-[400px] p-8">
        <div className="flex items-center gap-3 text-destructive">
          <AlertTriangle className="h-6 w-6" />
          <h2 className="text-lg font-semibold">{t('common:error')}</h2>
        </div>
        <p className="text-muted-foreground text-center max-w-md">
          {errorMessage}
        </p>
        <Button 
          variant="outline" 
          onClick={() => {
            window.location.reload();
          }}
        >
          {t('common:reload')}
        </Button>
      </div>
    );
  }

  return (
    <div className="page-container min-w-0 overflow-x-hidden">
      {/* Trial banner */}
      {trialDaysLeft !== null && (
        <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/40 px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-blue-800 dark:text-blue-200">
            <Clock className="h-5 w-5 shrink-0" />
            <span className="text-sm font-medium">
              {trialDaysLeft === 1
                ? t('dashboard:trialEndsTomorrow', 'Skúšobné obdobie končí zajtra')
                : t('dashboard:trialDaysLeft', { count: trialDaysLeft, defaultValue: 'Skúšobné obdobie končí o {{count}} dní' })}
            </span>
          </div>
          <Link href={getLocalizedUrl("/subscribe")}>
            <Button size="sm" variant="default" className="shrink-0">
              <CreditCard className="h-4 w-4 mr-1.5" />
              {t('plans:upgrade', 'Upgradovať plán')}
            </Button>
          </Link>
        </div>
      )}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6 sm:mb-8 pt-4" data-onboarding="dashboard-header">
        <div className="flex-1">
          <h1 className="page-title bg-gradient-to-r from-foreground to-primary bg-clip-text text-transparent">
            {t('dashboard:welcome')}
            {user?.companyName && user.companyName.trim() && user.companyName !== (user?.email?.split('@')[0] || '') ? `, ${user.companyName}` : ''}
          </h1>
          <div className="flex items-center gap-2 mt-2">
            <DashboardOnboardingTrigger />
          </div>
        </div>
        
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="outline" size="sm" className="gap-2 w-full sm:w-auto">
              <Settings className="h-4 w-4" />
              <span className="hidden xs:inline">{t('dashboard:editDashboard')}</span>
              <span className="xs:hidden">{t('common:edit')}</span>
            </Button>
          </SheetTrigger>
          <SheetContent>
            <SheetHeader>
              <SheetTitle>{t('dashboard:dashboardSettings')}</SheetTitle>
              <SheetDescription>
                {t('dashboard:selectSections')}
              </SheetDescription>
            </SheetHeader>
            <div className="py-4 space-y-4">
              {Array.isArray(sections) ? sections
                .sort((a, b) => a.order - b.order)
                .map((section) => (
                  <div key={section.id} className="flex items-center justify-between p-2 border rounded-md">
                    <div className="flex items-center space-x-2">
                      <Grip className="h-4 w-4 text-muted-foreground mr-1" />
                      <Checkbox 
                        id={`section-${section.id}`} 
                        checked={section.visible}
                        onCheckedChange={() => toggleSectionVisibility(section.id)}
                      />
                      <label
                        htmlFor={`section-${section.id}`}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        {section.title}
                      </label>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => moveSectionUp(section.id)}
                        disabled={
                          sections.filter(s => s.order < section.order).length === 0
                        }
                      >
                        <ChevronUp className="h-4 w-4" />
                        <span className="sr-only">{t('common:moveUp')}</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => moveSectionDown(section.id)}
                        disabled={
                          sections.filter(s => s.order > section.order).length === 0
                        }
                      >
                        <ChevronDown className="h-4 w-4" />
                        <span className="sr-only">Posunúť dole</span>
                      </Button>
                    </div>
                  </div>
              )) : null}
            </div>
          </SheetContent>
        </Sheet>
      </div>

      {/* Onboarding checklist - pre nových používateľov (nie v marketing mode) */}
      {!isMarketingMode && (
        <OnboardingChecklist />
      )}

      {/* Dynamicky zoradené sekcie dashboardu podľa nastaveného poradia */}
      {Array.isArray(sections) ? sections
        .sort((a, b) => a.order - b.order)
        .filter(section => section.visible)
        .map(section => {
          switch (section.id) {
            case 'progressRings':
              return (
                <div key={section.id} className="mb-6 sm:mb-8">
                  <Card>
                    <CardHeader className="pb-3 sm:pb-2">
                      <CardTitle className="text-lg sm:text-xl">Prehľad úloh</CardTitle>
                      <CardDescription className="text-sm">
                        Aktuálny stav vašich úloh a projektov
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                        {/* Dokončené úlohy */}
                        <div className="flex flex-col items-center justify-center p-3 sm:p-4">
                          <ProgressRing 
                            value={completedPercentage} 
                            size="md"
                            variant="success"
                            valuePrefix=""
                            label="dokončené"
                          />
                          <div className="mt-3 text-center">
                            <h3 className="font-medium flex items-center justify-center text-sm sm:text-base">
                              <CheckCircle className="h-4 w-4 mr-1.5 text-green-500" />
                              Dokončené úlohy
                            </h3>
                            <p className="text-xs sm:text-sm text-muted-foreground">
                              {displayCompleted} z {totalTasks} úloh
                            </p>
                          </div>
                        </div>
                        
                        {/* Označené úlohy - zvýraznený element */}
                        <div className="flex flex-col items-center justify-center p-3 sm:p-4 relative sm:col-span-2 lg:col-span-1">
                          <div className="absolute inset-0 bg-primary/5 dark:bg-primary/10 rounded-xl -z-10 hidden sm:block"></div>
                          <ProgressRing 
                            value={starredPercentage} 
                            size="md"
                            variant="warning"
                            valuePrefix=""
                            label="označené"
                            className="relative"
                          />
                          <div className="mt-3 text-center">
                            <h3 className="font-medium flex items-center justify-center text-sm sm:text-base">
                              <Star className="h-4 w-4 mr-1.5 text-yellow-500 fill-yellow-500" />
                              Označené úlohy
                            </h3>
                            <p className="text-xs sm:text-sm text-muted-foreground">
                              {displayStarred} z {totalTasks} úloh
                            </p>
                          </div>
                        </div>
                        
                        {/* Čakajúce úlohy */}
                        <div className="flex flex-col items-center justify-center p-3 sm:p-4">
                          <ProgressRing 
                            value={pendingPercentage} 
                            size="md"
                            variant="info"
                            valuePrefix=""
                            label="čakajúce"
                          />
                          <div className="mt-3 text-center">
                            <h3 className="font-medium flex items-center justify-center text-sm sm:text-base">
                              <HourglassIcon className="h-4 w-4 mr-1.5 text-blue-500" />
                              Čakajúce úlohy
                            </h3>
                            <p className="text-xs sm:text-sm text-muted-foreground">
                              {displayPending} z {totalTasks} úloh
                            </p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              );
            case 'weeklyCalendar':
              return (
                <Card key={section.id} className="mb-6 sm:mb-8">
                  <CardHeader className="pb-3 sm:pb-2">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
                      <CardTitle className="text-lg sm:text-xl font-medium flex items-center">
                        <Calendar className="h-5 w-5 mr-2 text-primary" />
                        Týždenný plánovač
                      </CardTitle>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="gap-2 w-full sm:w-auto"
                        onClick={() => {
                          const protocol = window.location.protocol;
                          const host = window.location.host;
                          const shareLink = `${protocol}//${host}/standalone-planner`;
                          
                          // Skopírujeme odkaz do schránky
                          navigator.clipboard.writeText(shareLink).then(() => {
                            toast({
                              title: "Odkaz skopírovaný",
                              description: "Odkaz na týždenný plánovač bol skopírovaný do schránky",
                            });
                          }).catch(err => {
                            console.error('Nepodarilo sa skopírovať odkaz:', err);
                            toast({
                              title: "Chyba pri kopírovaní",
                              description: "Nepodarilo sa skopírovať odkaz do schránky",
                              variant: "destructive",
                            });
                          });
                          
                          // Taktiež otvoríme standalone planner v novom okne
                          const screenWidth = window.screen.width;
                          const screenHeight = window.screen.height;
                          const width = Math.min(1400, screenWidth * 0.9);
                          const height = Math.min(900, screenHeight * 0.9);
                          const left = (screenWidth - width) / 2;
                          const top = (screenHeight - height) / 2;
                          
                          window.open(
                            shareLink, 
                            '_blank', 
                            `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes,menubar=no,toolbar=no,location=no,status=no`
                          );
                        }}
                      >
                          <Share2 className="h-4 w-4" />
                          <span className="hidden xs:inline">Zdieľať</span>
                          <span className="xs:hidden">Zdieľať</span>
                        </Button>
                    </div>
                    <CardDescription className="text-sm">
                      Drag-and-drop organizácia úloh na týždňový prehľad (nemení skutočné dátumy úloh)
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="px-3 sm:px-6">
                    <WeeklyCalendar tasks={starredTasks} />
                  </CardContent>
                </Card>
              );
            case 'stats':
              return (
                <div key={section.id} className="space-y-4 sm:space-y-6 mb-6 sm:mb-8" data-onboarding="task-statistics">

                  {/* Finančné štatistiky - responsívne */}
                  <div className="grid gap-4 sm:gap-6 lg:grid-cols-3">
                    {/* Hlavná suma s kruhowym grafom */}
                    <Card className="lg:col-span-2">
                      <CardHeader className="pb-3 sm:pb-4">
                        <CardTitle className="text-lg sm:text-xl font-medium flex items-center">
                          <CreditCard className="h-5 w-5 mr-2 text-green-600" />
                          Finančný prehľad úloh
                        </CardTitle>
                        <CardDescription className="text-sm">Rozdelenie hodnoty úloh podľa stavu</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                          {/* Kruhový graf - responsívny */}
                          <div className="relative">
                            <div className="w-32 h-32 sm:w-40 sm:h-40 rounded-full flex items-center justify-center bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 dark:from-green-900/20 dark:via-emerald-900/20 dark:to-teal-900/20 border-4 border-green-200 dark:border-green-700 shadow-lg">
                              <div className="text-center">
                                <div className="text-lg sm:text-2xl font-bold text-green-600">
                                  {taskStatistics?.totalTasksAmount ? 
                                    `${Number(taskStatistics?.totalTasksAmount ?? 0).toFixed(0)} €` : 
                                    '0 €'
                                  }
                                </div>
                                <div className="text-xs text-muted-foreground">Celková hodnota</div>
                              </div>
                            </div>
                            
                            {/* SVG kruhové grafy - responsívne */}
                            {taskStatistics?.amounts && taskStatistics.totalTasksAmount > 0 && (
                              <svg className="absolute inset-0 w-32 h-32 sm:w-40 sm:h-40 transform -rotate-90" viewBox="0 0 160 160">
                                <defs>
                                  <linearGradient id="completedGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#10b981" />
                                    <stop offset="100%" stopColor="#059669" />
                                  </linearGradient>
                                  <linearGradient id="pendingGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#3b82f6" />
                                    <stop offset="100%" stopColor="#1d4ed8" />
                                  </linearGradient>
                                  <linearGradient id="starredGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#f59e0b" />
                                    <stop offset="100%" stopColor="#d97706" />
                                  </linearGradient>
                                </defs>
                                
                                {/* Pozadie kruhu */}
                                <circle
                                  cx="80"
                                  cy="80"
                                  r="70"
                                  fill="none"
                                  stroke="#e5e7eb"
                                  strokeWidth="8"
                                  className="dark:stroke-gray-700"
                                />
                                
                                {/* Dokončené úlohy */}
                                <circle
                                  cx="80"
                                  cy="80"
                                  r="70"
                                  fill="none"
                                  stroke="url(#completedGradient)"
                                  strokeWidth="8"
                                  strokeLinecap="round"
                                  strokeDasharray={`${(taskStatistics.amounts.completed / taskStatistics.totalTasksAmount) * 440} 440`}
                                />
                                
                                {/* Čakajúce úlohy */}
                                <circle
                                  cx="80"
                                  cy="80"
                                  r="60"
                                  fill="none"
                                  stroke="url(#pendingGradient)"
                                  strokeWidth="6"
                                  strokeLinecap="round"
                                  strokeDasharray={`${(taskStatistics.amounts.pending / taskStatistics.totalTasksAmount) * 377} 377`}
                                  strokeDashoffset={`-${(taskStatistics.amounts.completed / taskStatistics.totalTasksAmount) * 377}`}
                                />
                                
                                {/* Označené úlohy */}
                                <circle
                                  cx="80"
                                  cy="80"
                                  r="50"
                                  fill="none"
                                  stroke="url(#starredGradient)"
                                  strokeWidth="4"
                                  strokeLinecap="round"
                                  strokeDasharray={`${(taskStatistics.amounts.starred / taskStatistics.totalTasksAmount) * 314} 314`}
                                  strokeDashoffset={`-${((taskStatistics.amounts.completed + taskStatistics.amounts.pending) / taskStatistics.totalTasksAmount) * 314}`}
                                />
                              </svg>
                            )}
                            
                            {/* Hover efekt */}
                            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-transparent to-green-100/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                          </div>

                          {/* Legenda s hodnotami */}
                          <div className="flex-1 space-y-4">
                            {taskStatistics?.amounts && (
                              <>
                                <div className="flex items-center justify-between p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-700">
                                  <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 bg-emerald-500 rounded-full"></div>
                                    <span className="font-medium">Dokončené úlohy</span>
                                  </div>
                                  <div className="text-right">
                                    <div className="font-bold text-emerald-600">{Number(taskStatistics?.amounts?.completed ?? 0).toFixed(2)} €</div>
                                    <div className="text-xs text-muted-foreground">{taskStatistics?.counts?.completed ?? 0} úloh</div>
                                  </div>
                                </div>

                                <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                                  <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                                    <span className="font-medium">Čakajúce úlohy</span>
                                  </div>
                                  <div className="text-right">
                                    <div className="font-bold text-blue-600">{Number(taskStatistics?.amounts?.pending ?? 0).toFixed(2)} €</div>
                                    <div className="text-xs text-muted-foreground">{taskStatistics?.counts?.pending ?? 0} úloh</div>
                                  </div>
                                </div>

                                <div className="flex items-center justify-between p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-700">
                                  <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                                    <span className="font-medium">Označené úlohy</span>
                                  </div>
                                  <div className="text-right">
                                    <div className="font-bold text-yellow-600">{Number(taskStatistics?.amounts?.starred ?? 0).toFixed(2)} €</div>
                                    <div className="text-xs text-muted-foreground">{taskStatistics?.counts?.starred ?? 0} úloh</div>
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Top klienti */}
                    <Card data-onboarding="top-clients">
                      <CardHeader>
                        <CardTitle className="text-lg font-medium flex items-center">
                          <Users className="h-5 w-5 mr-2 text-purple-600" />
                          Top klienti
                        </CardTitle>
                        <CardDescription>Najvyššie hodnoty úloh</CardDescription>
                      </CardHeader>
                      <CardContent>
                        {taskStatistics?.clientsWithTasks && taskStatistics.clientsWithTasks.length > 0 ? (
                          <div className="space-y-3">
                            {Array.isArray(taskStatistics.clientsWithTasks) ? taskStatistics.clientsWithTasks
                              .filter((client: any) => client && typeof client === 'object')
                              .sort((a: any, b: any) => (b.totalAmount || 0) - (a.totalAmount || 0))
                              .slice(0, 5)
                              .map((client: any, index: number) => {
                                const maxAmount = taskStatistics.clientsWithTasks[0]?.totalAmount || 1;
                                const percentage = (client.totalAmount / maxAmount) * 100;
                                
                                return (
                                  <div key={client.clientId} className="space-y-2">
                                    <div className="flex justify-between items-center">
                                      <div className="flex items-center gap-2">
                                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                                          index === 0 ? 'bg-yellow-500' : 
                                          index === 1 ? 'bg-gray-400' : 
                                          index === 2 ? 'bg-orange-500' : 'bg-purple-500'
                                        }`}>
                                          {index + 1}
                                        </div>
                                        <div>
                                          <p className="font-medium text-sm">{client.clientName}</p>
                                          <p className="text-xs text-muted-foreground">{client.tasksCount} úloh</p>
                                        </div>
                                      </div>
                                      <div className="text-right">
                                        <div className="font-bold text-purple-600 text-sm">
                                          {Number(client?.totalAmount ?? 0).toFixed(2)} €
                                        </div>
                                      </div>
                                    </div>
                                    {/* Progress bar */}
                                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                      <div 
                                        className={`h-2 rounded-full ${
                                          index === 0 ? 'bg-gradient-to-r from-yellow-400 to-yellow-600' : 
                                          index === 1 ? 'bg-gradient-to-r from-gray-300 to-gray-500' : 
                                          index === 2 ? 'bg-gradient-to-r from-orange-400 to-orange-600' : 'bg-gradient-to-r from-purple-400 to-purple-600'
                                        }`}
                                        style={{ width: `${percentage}%` }}
                                      />
                                    </div>
                                  </div>
                                );
                              }) : null}
                          </div>
                        ) : (
                          <div className="text-center py-8">
                            <Users className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                            <p className="text-sm text-muted-foreground">Žiadne úlohy s hodnotou</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                </div>
              );
            case 'starredTasks':
              return (
                <div key={section.id} className="mb-8" data-onboarding="starred-tasks">
                  <div className="grid gap-4 md:grid-cols-1">
                    <Card className="md:max-w-[600px] mx-auto w-full">
                      <CardHeader>
                        <CardTitle className="text-lg font-medium flex items-center">
                          <Star className="h-5 w-5 mr-2 text-yellow-500 fill-yellow-500" />
                          Označené úlohy
                        </CardTitle>
                        <CardDescription>Prioritné úlohy, na ktorých sa aktuálne pracuje</CardDescription>
                      </CardHeader>
                      <CardContent>
                        {starredTasks.length === 0 ? (
                          <p className="text-sm text-muted-foreground">Žiadne označené úlohy</p>
                        ) : (
                          <div className="space-y-4">
                            {Array.isArray(starredTasks) ? starredTasks.filter(task => task && task.id && task.title).map(task => {
                              const taskDueDate = task.dueDate || (task as any).due_date;
                              return (
                              <Link key={task.id} href={getLocalizedUrl("/tasks")}>
                                <div className="flex items-center gap-3 p-4 hover:bg-muted rounded-lg cursor-pointer border-l-4 border-primary bg-primary/5 dark:bg-primary/10 shadow-md hover:shadow-lg transition-all duration-200 hover:translate-x-1 backdrop-blur-sm">
                                  <div className="relative">
                                    <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
                                    <BellRing className="h-3 w-3 absolute -top-1 -right-1 text-primary" />
                                  </div>
                                  <div className="flex-1">
                                      <p className="font-semibold text-black dark:text-white">{task.title || 'Bez názvu'}</p>
                                    <div className="flex justify-between items-center mt-1">
                                      <p className="text-sm text-muted-foreground">
                                          Termín: {taskDueDate ? (new Date(taskDueDate).toLocaleDateString('sk-SK') || 'Neurčený') : 'Neurčený'}
                                      </p>
                                      <span className="inline-flex items-center rounded-full bg-primary/10 dark:bg-primary/20 px-3 py-1 text-xs font-medium text-primary border border-primary/20">
                                        Aktívne
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </Link>
                              );
                            }) : null}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                </div>
              );
            case 'reminders':
              return (
                <div key={section.id} className="mb-8">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <BellRing className="h-5 w-5" />
                        {t('reminders:list.title') || t('reminders:title') || 'Pripomienky'}
                      </CardTitle>
                      <CardDescription>
                        {t('reminders:list.description') || 'Vaše pripomienky a notifikácie'}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {remindersLoading ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="h-6 w-6 animate-spin" />
                          <span className="ml-2">Načítavam pripomienky...</span>
                        </div>
                      ) : reminders && reminders.length > 0 ? (
                        <div className="space-y-3">
                          {Array.isArray(reminders) ? reminders.slice(0, 5).map((reminder: any) => (
                            <div key={reminder.id} className="flex items-center justify-between p-3 border rounded-lg">
                              <div className="flex-1">
                                <h4 className="font-medium">{reminder.title}</h4>
                                {reminder.description && (
                                  <p className="text-sm text-muted-foreground mt-1">
                                    {reminder.description}
                                  </p>
                                )}
                                <p className="text-xs text-muted-foreground mt-1">
                                  {new Date(reminder.dueAt).toLocaleString('sk-SK')}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                {reminder.channels?.includes('in_app') && (
                                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                    V aplikácii
                                  </span>
                                )}
                                {reminder.channels?.includes('email') && (
                                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                                    Email
                                  </span>
                                )}
                                {reminder.channels?.includes('push') && (
                                  <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">
                                    Push
                                  </span>
                                )}
                              </div>
                            </div>
                          )) : null}
                          {reminders.length > 5 && (
                            <div className="text-center pt-2">
                              <Link href={getLocalizedUrl("/profile") + "?tab=reminders"}>
                                <Button variant="outline" size="sm">
                                  Zobraziť všetky ({reminders.length})
                                </Button>
                              </Link>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-muted-foreground">
                          <BellRing className="h-12 w-12 mx-auto mb-4 opacity-50" />
                          <p>{t('reminders:list.noReminders') || t('reminders:noReminders') || 'Žiadne pripomienky'}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              );
            case 'subscription':
              return (
                <div key={section.id} className="mb-8">
                  <PlanInfoWidget />
                </div>
              );
            default:
              return null;
          }
        }) : null}
      
      {/* Onboarding Demo Panel - len pre admin testovanie */}
      {user?.role === 'admin' && (
        <div className="mt-8 pt-8 border-t">
          {/* Demo component removed */}
        </div>
      )}
    </div>
  );
}
